/**
 * Map gimmicks (Phase 2) — deterministic tile modifiers.
 *
 * Configuration (no in-game randomness):
 *   config.gimmicks = [{ type: 'ice' | 'swamp', col, row }, ...]
 *
 * - ice   : entering the tile forces the pawn to keep sliding in the entry
 *           direction; it stops at a wall, a pawn, the board edge, or on the
 *           first non-ice tile it reaches.
 * - swamp : ending a movement on the tile blocks pawn movement on the
 *           player's NEXT own turn (walls and non-movement abilities are
 *           still allowed). Recorded as players[i].effects.swamped — the
 *           swamp itself never affects path connectivity (delay only), so
 *           it is invisible to the path-existence BFS.
 *
 * The explorer's passive ("ignoreGimmicks") is expressed by callers passing
 * gimmickMap = null for that player — this module never inspects characters.
 *
 * Pure functions only; no dependency on the full game-state shape.
 */

import { stepBlockedByWall } from './walls.js';

/** Stable string key for a board cell. */
export function cellKey(col, row) {
  return col + ',' + row;
}

/** Build a Map 'col,row' -> gimmick type for O(1) tile lookup. */
export function buildGimmickMap(gimmicks) {
  const map = new Map();
  for (const g of gimmicks ?? []) {
    map.set(cellKey(g.col, g.row), g.type);
  }
  return map;
}

/** Gimmick type at a cell, or null. */
export function gimmickTypeAt(gimmickMap, col, row) {
  return gimmickMap.get(cellKey(col, row)) ?? null;
}

/** Is there a swamp tile at this cell? (plain array scan; used by applyMove) */
export function isSwampAt(gimmicks, col, row) {
  for (const g of gimmicks ?? []) {
    if (g.type === 'swamp' && g.col === col && g.row === row) return true;
  }
  return false;
}

/**
 * Resolve forced ice sliding: a pawn that lands on `cell` moving in
 * direction (dc, dr) keeps sliding while standing on ice, stopping when
 * the next cell is off-board, walled off, or occupied — or as soon as it
 * comes to rest on a non-ice tile. Returns the final resting cell.
 *
 * - `occupied` may be null (path-existence BFS ignores pawns, mirroring the
 *   classic rule that pawns never block the path).
 * - Sliding never crosses walls and never consumes ninja jumps.
 * - A diagonal entry (both dc and dr non-zero — only reachable via the
 *   diagonalStep ability) does not slide: diagonal sliding is undefined by
 *   the wall geometry, and the only diagonal mover (explorer) is immune to
 *   gimmicks anyway.
 */
export function resolveSlide(boardSize, wallSet, gimmickMap, occupied, cell, dc, dr) {
  if (!gimmickMap) return cell;
  if (dc === 0 && dr === 0) return cell;
  if (dc !== 0 && dr !== 0) return cell; // diagonal entry: no slide

  let cur = cell;
  while (gimmickMap.get(cellKey(cur.col, cur.row)) === 'ice') {
    const col = cur.col + dc;
    const row = cur.row + dr;
    if (col < 0 || row < 0 || col >= boardSize || row >= boardSize) break; // board edge
    if (stepBlockedByWall(wallSet, cur, { col, row })) break; // wall
    if (occupied && occupied.has(cellKey(col, row))) break; // pawn
    cur = { col, row };
  }
  return cur;
}
